import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  calculateInvestmentValueAtMonthFromStrategy,
  getInvestmentGrowthDecimalFromStrategy,
} from './summary/summaryInvestmentAtMonth';
import SummaryFeedbackForm from './summary/SummaryFeedbackForm';

/** Set to true to show the "We horen graag jouw mening" feedback module. */
const SHOW_FEEDBACK_MODULE = false;

const SummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('samenvatting');
  
  // Get strategy data from location state or sessionStorage
  let strategyData = location.state?.strategyData;
  if (!strategyData) {
    const storedData = sessionStorage.getItem('sparroStrategyData');
    if (storedData) {
      strategyData = JSON.parse(storedData);
    }
  }

  useEffect(() => {
    document.title = 'Slimmer af - Samenvatting';
    if (!strategyData) {
      // If no data available, redirect to portal
      navigate('/portal');
      return;
    }

    // Set up scroll listener to update active section (throttled to prevent flicker)
    let rafId = null;
    let lastTs = 0;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const now = Date.now();
        if (now - lastTs < 100) return; // throttle
        lastTs = now;
        const sections = ['samenvatting', 'aflosschema', 'betaalschema', 'faq'];
        const scrollPosition = window.scrollY + 200;

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i]);
          if (section && section.offsetTop <= scrollPosition) {
            setActiveSection(sections[i]);
            break;
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [strategyData, navigate]);

  if (!strategyData) return null;

  const fromPortalSaved = location.state?.fromPortalSaved === true;
  const PORTAL_GRADIENT = { background: 'linear-gradient(135deg, #3399FF 0%, #2288dd 100%)' };
  const TOP_BAR_HEIGHT = 56;

  const {
    totalDebt,
    repaymentSystem,
    minPayment,
    monthlyPayment,
    enableInvestment,
    monthlyInvestment,
    startingInvestmentCapital,
    payoffMonths,
    totalCost,
    totalCostWithoutInvesting,
    interestAtEnd,
    userName,
    payoffByInvestment,
    enableDelay,
    delayYears,
    delayMonths,
    totalTermYears,
    finalInvestmentValue,
    enableGrowth,
    growthRate,
    enableSeparateGrowth,
    investmentGrowthRate,
    chartData: monthlyChartData,  // Monthly data for graph
    tableData: yearlyTableData,    // Yearly data for table
    oneTimeRepayments = []         // Eenmalige aflossingen per jaar (voor kolom in betaalschema)
  } = strategyData;

  // Use tableData if available (for backward compatibility), otherwise fall back to chartData
  const chartData = {
    rows: yearlyTableData || monthlyChartData || [],
    payoffByInvestment: payoffByInvestment || false
  };

  const payoffYears = Math.floor(payoffMonths / 12);
  const payoffRemainingMonths = Math.round(payoffMonths % 12);
  const availableForInvestment = Math.max(0, monthlyPayment - minPayment);
  const investmentAmount = enableInvestment ? Math.min(monthlyInvestment, availableForInvestment) : 0;
  const actualLoanPayment = monthlyPayment - investmentAmount;

  const getInvestmentGrowthRateValue = () =>
    getInvestmentGrowthDecimalFromStrategy({
      enableGrowth,
      enableSeparateGrowth,
      investmentGrowthRate,
      growthRate,
    });

  const calculateInvestmentValueAtMonth = (targetMonth) =>
    calculateInvestmentValueAtMonthFromStrategy(strategyData, targetMonth);

  // Determine strategy type
  let strategyType = 'Volledige focus op afbetalen';
  if (enableInvestment) {
    // If investment is enabled but amount is 0 and no starting capital, treat as focus on paying off
    const hasStartingCapital = startingInvestmentCapital && Number(startingInvestmentCapital) > 0;
    if (monthlyInvestment === 0 && !hasStartingCapital) {
      strategyType = 'Volledige focus op afbetalen';
    } else {
      const investmentRatio = availableForInvestment > 0 ? (monthlyInvestment / availableForInvestment) : 0;
      if (investmentRatio >= 0.8) {
        strategyType = 'Volledige focus op beleggen';
      } else if (investmentRatio >= 0.2) {
        strategyType = 'Gemengde focus: afbetalen en beleggen';
      } else {
        strategyType = 'Focus op afbetalen met beperkt beleggen';
      }
    }
  }

  const sections = [
    { 
      id: 'samenvatting', 
      label: '1. Samenvatting',
      renderIcon: (isActive) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="18" rx="1" fill={isActive ? '#FFFFFF' : '#9CA3AF'} />
          <rect x="12" y="8" width="4" height="13" rx="1" fill={isActive ? '#FFFFFF' : '#9CA3AF'} />
          <rect x="18" y="12" width="3" height="9" rx="1" fill={isActive ? '#FFFFFF' : '#9CA3AF'} />
        </svg>
      )
    },
    { 
      id: 'aflosschema', 
      label: '2. Aflosschema',
      renderIcon: (isActive) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 17 L8 12 L13 15 L21 7" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 7 L21 12" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
          <path d="M16 7 L21 7" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    },
    { 
      id: 'betaalschema', 
      label: '3. Betaalschema',
      renderIcon: (isActive) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="5" width="20" height="14" rx="2" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" />
          <path d="M2 10 L22 10" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" />
          <circle cx="7" cy="14" r="1.5" fill={isActive ? '#FFFFFF' : '#9CA3AF'} />
        </svg>
      )
    },
    { 
      id: 'faq', 
      label: '4. FAQ',
      renderIcon: (isActive) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" />
          <path d="M12 17 L12 17.5" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2.5" strokeLinecap="round" />
          <path d="M12 13.5 L12 13 C12 11.5 13.5 11 13.5 9.5 C13.5 8.5 12.8 7.5 12 7.5 C11.2 7.5 10.5 8.2 10.5 9" stroke={isActive ? '#FFFFFF' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    }
  ];

  const blueGradient = 'linear-gradient(135deg, #3399FF 0%, #2288dd 100%)';

  const summaryCards = [
    {
      title: 'Openstaande lening',
      value: `€${totalDebt.toLocaleString('nl-NL')}`,
      icon: '💳',
      color: '#3B82F6'
    },
    {
      title: 'Aflosstelsel',
      value: repaymentSystem === 15 ? '15 jaar' : '35 jaar',
      icon: '⏱️',
      color: '#10B981'
    },
    {
      title: 'Gekozen strategie',
      value: strategyType,
      icon: '🎯',
      color: '#3399FF'
    },
    {
      title: 'Minimale aflossing DUO',
      value: `€${minPayment.toLocaleString('nl-NL')}/maand`,
      icon: '📋',
      color: '#F59E0B'
    },
    {
      title: 'Gekozen maandelijkse betaling',
      value: `€${monthlyPayment.toLocaleString('nl-NL')}/maand`,
      subtitle: strategyData?.growthRate && Number(strategyData.growthRate) > 0 
        ? `Maandbedrag stijgt jaarlijks met ${Number(strategyData.growthRate)}%`
        : 'Geen jaarlijkse stijging',
      icon: '💵',
      color: '#EC4899'
    },
    {
      title: 'Gekozen maandelijkse investering',
      value: (enableInvestment && monthlyInvestment > 0) ? `€${Math.round(monthlyInvestment).toLocaleString('nl-NL')}/maand` : '€0',
      subtitle: (() => {
        if (!enableInvestment || monthlyInvestment <= 0) {
          return 'Geen investering';
        }
        // Check if there's investment growth using the same logic as getInvestmentGrowthRateValue
        if (enableGrowth) {
          if (enableSeparateGrowth && investmentGrowthRate && Number(investmentGrowthRate) > 0) {
            return `Investering stijgt jaarlijks met ${Number(investmentGrowthRate)}%`;
          } else if (growthRate && Number(growthRate) > 0) {
            return `Investering stijgt jaarlijks met ${Number(growthRate)}%`;
          }
        }
        return 'Geen jaarlijkse stijging';
      })(),
      icon: '📈',
      color: '#10B981'
    },
    {
      title: 'Beoogde tijd tot aflossing',
      value: (() => {
        const finalBalance = chartData.rows.length > 0 ? chartData.rows[chartData.rows.length - 1].principal : 0;
        const isNotPaidOff = payoffMonths >= repaymentSystem * 12 && finalBalance > 0;
        const isExactTerm = Math.abs(payoffMonths - repaymentSystem * 12) < 0.5 && finalBalance <= 0.01;
        
        if (isNotPaidOff) {
          return 'Niet binnen termijn';
        } else if (isExactTerm) {
          return `Exact ${repaymentSystem} jaar`;
        } else {
          return payoffRemainingMonths > 0 
            ? `${payoffYears} jaar ${payoffRemainingMonths} maanden`
            : `${payoffYears} jaar`;
        }
      })(),
      icon: '🎯',
      color: '#14B8A6',
      highlight: true
    },
    {
      title: 'Totaal af te betalen',
      value: `€${Math.round(totalCost).toLocaleString('nl-NL')}`,
      subtitle: 'Inclusief rente',
      icon: '💰',
      color: '#14B8A6',
      highlight: true
    },
    {
      title: 'Totaal te betalen rente',
      value: `€${Math.round(interestAtEnd).toLocaleString('nl-NL')}`,
      icon: '💸',
      color: '#14B8A6',
      highlight: true
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#e5e3e3',
      padding: '0',
      margin: '0',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      overflowX: 'hidden'
    }}>
      {/* Top bar: portal-style (from opgeslagen strategieën) or logo + Terug naar Portal */}
      <header style={{
        flexShrink: 0,
        height: fromPortalSaved ? TOP_BAR_HEIGHT : 64,
        background: '#FFFFFF',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: 'auto', display: 'block' }} />
        </a>
        {fromPortalSaved ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <button
              type="button"
              onClick={() => navigate('/portal')}
              style={{
                padding: '10px 20px',
                borderRadius: 9999,
                border: 'none',
                ...PORTAL_GRADIENT,
                color: '#FFFFFF',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                boxShadow: '0 2px 8px rgba(51,153,255,0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              Sla strategie op
            </button>
            <div style={{ width: 1, height: 24, background: '#e5e7eb', margin: '0 20px', flexShrink: 0 }} aria-hidden />
            <nav style={{ display: 'flex', alignItems: 'center', gap: 'clamp(12px, 2vw, 24px)' }}>
              <button
                type="button"
                onClick={() => navigate('/portal')}
                style={{
                  padding: '8px 0',
                  border: 'none',
                  background: 'transparent',
                  color: '#4b5563',
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
              >
                Strategie samenstellen
              </button>
              <button
                type="button"
                onClick={() => navigate('/portal', { state: { openSaved: true } })}
                style={{
                  padding: '10px 20px',
                  borderRadius: 9999,
                  border: 'none',
                  ...PORTAL_GRADIENT,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  boxShadow: '0 2px 8px rgba(51,153,255,0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                Opgeslagen strategieën
              </button>
              <a
                href="/#faq"
                style={{
                  padding: '8px 0',
                  textDecoration: 'none',
                  background: 'transparent',
                  color: '#4b5563',
                  fontSize: 15,
                  fontWeight: 500,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
              >
                FAQ
              </a>
            </nav>
          </div>
        ) : (
          <button
            onClick={() => navigate('/portal')}
            style={{
              padding: '10px 20px',
              borderRadius: 9999,
              border: 'none',
              background: 'linear-gradient(135deg, #3399FF 0%, #2288dd 100%)',
              color: '#FFFFFF',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              boxShadow: '0 2px 8px rgba(51,153,255,0.3)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(51,153,255,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(51,153,255,0.3)';
            }}
          >
            <span>←</span>
            <span>Terug naar Portal</span>
          </button>
        )}
      </header>

      {/* Main Content - full width (geen linkersidebar) */}
      <div style={{
        padding: '16px 40px 20px 40px',
        minHeight: `calc(100vh - ${fromPortalSaved ? TOP_BAR_HEIGHT : 64}px)`,
        overflowX: 'hidden',
        maxWidth: '100vw',
        width: '100%',
        position: 'relative',
        display: 'block',
        boxSizing: 'border-box'
      }}>
        {/* Floating Feedback Box — hidden for now; set SHOW_FEEDBACK_MODULE to true to show */}
        {SHOW_FEEDBACK_MODULE && <SummaryFeedbackForm strategyData={strategyData} activeSection={activeSection} />}
        {fromPortalSaved && (
          <button
            type="button"
            onClick={() => navigate('/portal', { state: { openSaved: true } })}
            style={{
              marginBottom: 16,
              padding: '8px 0',
              border: 'none',
              background: 'transparent',
              color: '#4b5563',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#3399FF'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#4b5563'; }}
          >
            <span>←</span>
            <span>Terug naar opgeslagen strategieën</span>
          </button>
        )}
        <div style={{
            transform: 'translateX(-30px) scale(0.8)',
          transformOrigin: 'top center',
          width: '100%',
          margin: '0 auto'
        }}>
        {/* Section 1: Samenvatting */}
        <div id="samenvatting" style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto 40px auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          padding: '50px 40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 'auto'
        }}>
          {/* Header */}
          <div style={{
            marginBottom: '50px'
          }}>
            <h1 style={{
              fontSize: '42px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 16px 0'
            }}>
              Jouw Persoonlijke Aflosstrategie
            </h1>
            <p style={{
              fontSize: '18px',
              color: '#6B7280',
              lineHeight: '1.6',
              margin: 0,
              maxWidth: '900px'
            }}>
              Welkom bij jouw op maat gemaakte aflosplan. Hieronder vind je een compleet overzicht 
              van jouw studieschuld, gekozen strategie en de verwachte uitkomsten. Dit rapport helpt 
              je om bewuste financiële keuzes te maken en grip te houden op jouw toekomst.
            </p>
          </div>

          {/* Summary Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px'
          }}>
            {summaryCards.map((card, index) => (
              <div
                key={index}
                style={{
                  background: card.highlight ? blueGradient : '#FFFFFF',
                  border: card.highlight ? 'none' : '2px solid #F3F4F6',
                  borderRadius: '16px',
                  padding: '28px',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: card.highlight ? '0 25px 60px rgba(83,113,255,0.3)' : 'none'
                }}
              >
                {/* Icon Circle */}
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  backgroundColor: card.highlight ? 'rgba(255,255,255,0.2)' : `${card.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  fontSize: '28px',
                  color: card.highlight ? '#FFFFFF' : 'inherit'
                }}>
                  {card.icon}
                </div>

                {/* Title */}
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: card.highlight ? 'rgba(255,255,255,0.8)' : '#6B7280',
                  margin: '0 0 12px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {card.title}
                </h3>

                {/* Value */}
                <p style={{
                  fontSize: card.highlight ? '28px' : '24px',
                  fontWeight: '700',
                  color: card.highlight ? '#FFFFFF' : '#1F2937',
                  margin: '0 0 8px 0',
                  lineHeight: '1.2'
                }}>
                  {card.value}
                </p>

                {/* Subtitle */}
                {card.subtitle && (
                  <p style={{
                    fontSize: '15px',
                    fontWeight: '700',
                      color: card.highlight ? 'rgba(255,255,255,0.85)' : '#4B5563',
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    {card.subtitle}
                  </p>
                )}

                {/* Highlight Badge */}
                {card.highlight && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#FFFFFF',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Resultaat
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: Aflosschema */}
        <div id="aflosschema" style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto 40px auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          padding: '50px 40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 'auto'
        }}>
          <h1 style={{
            fontSize: '42px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Aflosschema
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '30px'
          }}>
            Visualisatie van jouw aflosplan over de tijd
          </p>

          {/* Chart and Infographics Container */}
          <div style={{
            display: 'flex',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {/* Chart */}
            <div style={{
              backgroundColor: '#F9FAFB',
              borderRadius: '16px',
              padding: '30px 40px',
              flex: '1'
            }}>
              <svg width="100%" height="520" viewBox="0 0 1000 520" preserveAspectRatio="xMinYMid meet">
              {(() => {
                // Use monthly data for graph (smooth lines), fallback to yearly if not available
                const data = monthlyChartData || chartData.rows || [];
                if (!data || data.length === 0) return null;

                const maxPrincipal = Math.max(...data.map(d => d.principal || 0));
                const maxInterest = Math.max(...data.map(d => d.interest || 0));
                const maxInvestment = enableInvestment ? Math.max(...data.map(d => d.investment || 0)) : 0;
                const maxY = Math.max(maxPrincipal, maxInterest, maxInvestment) * 1.1;
                // Use the full repayment system years (15 or 35) for x-axis
                const maxYear = repaymentSystem;

                const xScale = (year) => 70 + (year / maxYear) * 860;
                const yScale = (value) => 450 - (value / maxY) * 380;

                // Generate paths using monthly data points (smooth lines)
                const balancePath = data.map((d, i) => 
                  `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.principal)}`
                ).join(' ');

                const interestPath = data.map((d, i) => 
                  `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.interest)}`
                ).join(' ');

                const investmentPath = enableInvestment ? data.map((d, i) => 
                  `${i === 0 ? 'M' : 'L'} ${xScale(d.year)} ${yScale(d.investment || 0)}`
                ).join(' ') : '';

                return (
                  <g>
                    {/* Define gradient for area fill */}
                    <defs>
                      <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3399FF" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#3399FF" stopOpacity="0.02" />
                      </linearGradient>
                    </defs>

                    {/* Grid lines */}
                    {(() => {
                      const formatYAxisLabel = (value) => {
                        if (value >= 1000) {
                          return `€${Math.round(value / 1000)}k`;
                        }
                        return `€${Math.round(value).toLocaleString('nl-NL')}`;
                      };
                      return [0, 0.25, 0.5, 0.75, 1].map(ratio => (
                        <g key={ratio}>
                          <line
                            x1="70" y1={70 + ratio * 380}
                            x2="930" y2={70 + ratio * 380}
                            stroke="#E5E7EB"
                            strokeWidth="1"
                          />
                          <text
                            x="55" y={70 + ratio * 380 + 6}
                            fontSize="18" fill="#9CA3AF" textAnchor="end"
                          >
                            {formatYAxisLabel(maxY * (1 - ratio))}
                          </text>
                        </g>
                      ));
                    })()}

                    {/* X-axis labels */}
                    {[0, 0.25, 0.5, 0.75, 1].map(ratio => (
                      <text
                        key={ratio}
                        x={70 + ratio * 860} y="470"
                        fontSize="18" fill="#9CA3AF" textAnchor="middle"
                      >
                        {Math.round(maxYear * ratio)}j
                      </text>
                    ))}

                    {/* Y-axis title */}
                    <text
                      x="10"
                      y="260"
                      fontSize="18"
                      fill="#374151"
                      fontWeight="700"
                      textAnchor="middle"
                      transform="rotate(-90 10 260)"
                    >
                      Euro
                    </text>

                    {/* X-axis title */}
                    <text
                      x="500"
                      y="495"
                      fontSize="18"
                      fill="#374151"
                      fontWeight="700"
                      textAnchor="middle"
                    >
                      Jaar
                    </text>

                    {/* Area fill under balance line */}
                    <path
                      d={`${balancePath} L ${xScale(data[data.length - 1].year)} 450 L ${xScale(data[0].year)} 450 Z`}
                      fill="url(#balanceGradient)"
                    />

                    {/* Balance line (blue) */}
                    <path
                      d={balancePath}
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />

                    {/* Interest line (gray) */}
                    <path
                      d={interestPath}
                      fill="none"
                      stroke="#94A3B8"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />

                    {/* Investment line (green, dashed) */}
                    {enableInvestment && investmentPath && (
                      <path
                        d={investmentPath}
                        fill="none"
                        stroke="#10B981"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="8,4"
                      />
                    )}

                    {/* Legend */}
                    <g transform="translate(70, 30)">
                      <circle cx="0" cy="0" r="5" fill="#3B82F6" />
                      <text x="12" y="4" fontSize="18" fill="#374151" fontWeight="500">Openstaande schuld</text>
                      
                      <circle cx="220" cy="0" r="5" fill="#94A3B8" />
                      <text x="232" y="4" fontSize="18" fill="#374151" fontWeight="500">Betaalde rente</text>
                      
                      {enableInvestment && (
                        <>
                          <circle cx="440" cy="0" r="5" fill="#10B981" />
                          <text x="452" y="4" fontSize="18" fill="#374151" fontWeight="500">Investeringen</text>
                        </>
                      )}
                    </g>

                    {/* Payoff indicator */}
                    {chartData.payoffByInvestment && data.length > 0 && (
                      <>
                        <circle
                          cx={xScale(data[data.length - 1].year)}
                          cy={yScale(data[data.length - 1].principal)}
                          r="6"
                          fill="#10B981"
                          stroke="#FFFFFF"
                          strokeWidth="2"
                        />
                        <text
                          x={xScale(data[data.length - 1].year)}
                          y={yScale(data[data.length - 1].principal) - 15}
                          fontSize="14"
                          fill="#10B981"
                          fontWeight="600"
                          textAnchor="middle"
                        >
                          Afbetaald ✓
                        </text>
                      </>
                    )}
                  </g>
                );
              })()}
            </svg>
          </div>

          {/* Infographic Summary Boxes */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '280px',
            flexShrink: 0
          }}>
            {/* Totale Kosten */}
            <div style={{
              background: blueGradient,
              borderRadius: '16px',
              padding: '18px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 20px 50px rgba(83,113,255,0.25)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0
              }}>
                💰
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.85)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Totale Kosten
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#FFFFFF'
                }}>
                  €{Math.round(totalCost).toLocaleString('nl-NL')}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: '1.4'
                }}>
                  {enableInvestment 
                    ? 'Incl. aflossing én investering' 
                    : 'Totaal uitgegeven aan betalingen'}
                </div>
              </div>
            </div>

            {/* Totaal Betaalde Rente */}
            <div style={{
              background: blueGradient,
              borderRadius: '16px',
              padding: '18px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 20px 50px rgba(83,113,255,0.25)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0
              }}>
                📊
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.85)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Waarvan Betaalde Rente
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#FFFFFF'
                }}>
                  €{Math.round(interestAtEnd).toLocaleString('nl-NL')}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: '1.4'
                }}>
                  {((interestAtEnd / totalDebt) * 100).toFixed(1)}% van schuld
                </div>
              </div>
            </div>

            {/* Looptijd */}
            <div style={{
              background: blueGradient,
              borderRadius: '16px',
              padding: '18px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 20px 50px rgba(83,113,255,0.25)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0
              }}>
                ⏱️
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.85)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Looptijd
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#FFFFFF'
                }}>
                  {(() => {
                    const finalBalance = chartData.rows.length > 0 ? chartData.rows[chartData.rows.length - 1].principal : 0;
                    const isNotPaidOff = payoffMonths >= repaymentSystem * 12 && finalBalance > 0;
                    const isExactTerm = Math.abs(payoffMonths - repaymentSystem * 12) < 0.5 && finalBalance <= 0.01;
                    
                    if (isNotPaidOff) {
                      return <span style={{ color: '#FFB3B3' }}>Niet binnen termijn</span>;
                    } else if (isExactTerm) {
                      return `Exact ${repaymentSystem} jaar`;
                    } else {
                      return `${payoffYears} jaar ${payoffRemainingMonths} ${payoffRemainingMonths === 1 ? 'maand' : 'maanden'}`;
                    }
                  })()}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.8)',
                  lineHeight: '1.4'
                }}>
                  {(() => {
                    const finalBalance = chartData.rows.length > 0 ? chartData.rows[chartData.rows.length - 1].principal : 0;
                    const isNotPaidOff = payoffMonths >= repaymentSystem * 12 && finalBalance > 0;
                    return isNotPaidOff ? 'Schuld niet volledig afgelost binnen termijn' : 'Tot schuld volledig is afgelost';
                  })()}
                </div>
              </div>
            </div>

            {/* Waarde Investeringen */}
            {enableInvestment && (
              <div style={{
                background: blueGradient,
                borderRadius: '16px',
                padding: '18px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 20px 50px rgba(83,113,255,0.25)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}>
                  📈
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'rgba(255,255,255,0.85)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '4px'
                  }}>
                    Waarde Investeringen
                  </div>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#FFFFFF'
                  }}>
                    €{Math.round(strategyData.finalInvestmentValue || 0).toLocaleString('nl-NL')}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: '1.4'
                  }}>
                    Op moment van aflossing
                  </div>
                </div>
              </div>
            )}

            {/* Totaal te betalen rente */}
            <div style={{
              background: blueGradient,
              borderRadius: '16px',
              padding: '18px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 20px 50px rgba(83,113,255,0.25)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                flexShrink: 0
              }}>
                💸
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.85)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}>
                  Totaal te betalen rente
                </div>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#FFFFFF'
                }}>
                  €{Math.round(interestAtEnd).toLocaleString('nl-NL')}
                </div>
              </div>
            </div>
          </div>
        </div>

          {/* Explanatory text */}
          <div style={{
            backgroundColor: '#F9FAFB',
            border: '2px solid #E5E7EB',
            borderRadius: '16px',
            padding: '32px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 20px 0'
            }}>
              Analyse van jouw aflosstrategie
            </h3>
            
            <div style={{
              fontSize: '18px',
              color: '#374151',
              lineHeight: '1.8',
              margin: 0
            }}>
              <p style={{ marginTop: 0, marginBottom: '16px' }}>
                <strong>Overzicht van je schuldontwikkeling:</strong> Deze grafiek visualiseert 
                hoe jouw studieschuld van €{totalDebt.toLocaleString('nl-NL')} zich ontwikkelt 
                over een periode van {repaymentSystem} jaar. De blauwe lijn toont je openstaande 
                schuld, die gestaag daalt door jouw maandelijkse aflossingen van 
                €{monthlyPayment.toLocaleString('nl-NL')}
                {strategyData?.enableGrowth && Number(strategyData?.growthRate || 0) > 0 ? (
                  <> die jaarlijks met {Number(strategyData.growthRate)}% stijgen. </>
                ) : <>.</>} Met deze strategie ben je volledig 
                schuldenvrij binnen {payoffYears} jaar en {payoffRemainingMonths} maanden.
              </p>

              <p style={{ marginBottom: '16px' }}>
                <strong>Rentekosten:</strong> De grijze lijn toont de cumulatieve rente die je 
                betaalt over de looptijd van je lening. In totaal betaal je 
                €{interestAtEnd.toLocaleString('nl-NL', { maximumFractionDigits: 0 })} aan rente, wat {((interestAtEnd / totalDebt) * 100).toFixed(1)}%{' '}
                van je oorspronkelijke schuld bedraagt.                 Dit komt door de {repaymentSystem === 15 ? '2,21%' : '2,57%'}{' '}
                jaarlijkse rente die maandelijks wordt berekend over je resterende schuld.
              </p>

              {enableInvestment ? (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    <strong>Investeringsstrategie:</strong> Je hebt gekozen voor een gemengde aanpak 
                    waarbij je €{Math.round(monthlyInvestment).toLocaleString('nl-NL')} per maand investeert 
                    investeert in de aandelenmarkt. De groene stippellijn toont hoe je investeringen 
                    groeien met een verwacht rendement van 6% per jaar (conservatieve schatting, inclusief transactiekosten). Dit betekent dat je zult beginnen 
                    met €{actualLoanPayment.toFixed(0)}{' '}maandelijks aflossen en €{investmentAmount.toFixed(0)}{' '}
                    maandelijks investeren
                    {strategyData?.enableGrowth && Number(strategyData?.growthRate || 0) > 0 ? (
                      <>, wat naarmate je aflossingen jaarlijks met {Number(strategyData.growthRate)}% stijgen over de jaren toe zal nemen.</>
                    ) : (
                      <>.</>
                    )}
                  </p>

                  {chartData.payoffByInvestment ? (
                    <p style={{ marginBottom: '16px' }}>
                      <strong>Aflosmoment via investering:</strong> Door jouw investeringsstrategie 
                      bereik je een cruciaal keerpunt: op het moment dat je investeringen een waarde 
                      bereiken die gelijk is aan of groter is dan je resterende studieschuld, kun je 
                      de volledige lening in één keer aflossen. Dit gebeurt eerder dan wanneer je 
                      alleen zou aflossen.
                    </p>
                  ) : (
                    <p style={{ marginBottom: '16px' }}>
                      <strong>Balans tussen aflossen en investeren:</strong> Met jouw gekozen 
                      investeringspercentage bouw je parallel vermogen op terwijl je je schuld aflost. 
                      Aan het einde van de looptijd heb je niet alleen je schuld afbetaald, maar ook 
                      een investeringsportefeuille opgebouwd ter waarde van ongeveer 
                      €{strategyData.finalInvestmentValue?.toLocaleString('nl-NL') || '0'}.
                    </p>
                  )}

                  <p style={{ marginBottom: 0 }}>
                    <strong>Risico's en overwegingen:</strong> Deze strategie biedt potentieel voor 
                    vermogensopbouw, maar houdt ook risico's in. Aandelenmarkten kunnen fluctueren, 
                    en het verwachte rendement van 6% is niet gegarandeerd. In tijden van economische 
                    neergang kunnen je investeringen tijdelijk in waarde dalen. Daarom is het belangrijk 
                    dat je altijd je minimale DUO-aflossing blijft voldoen en alleen investeert met 
                    geld dat je voor de middellange tot lange termijn kunt missen.
                  </p>
                </>
              ) : (
                <>
                  <p style={{ marginBottom: '16px' }}>
                    <strong>Conservatieve aflosaanpak:</strong> Je hebt gekozen voor een volledige 
                    focus op het aflossen van je studieschuld zonder te investeren. Dit is de meest 
                    voorspelbare en risicoloze strategie. Door maandelijks consequent 
                    €{monthlyPayment.toLocaleString('nl-NL')} af te lossen, zorg je voor een stabiele 
                    en lineaire afname van je schuld zonder blootstelling aan marktrisico's.
                  </p>

                  <p style={{ marginBottom: 0 }}>
                    <strong>Voordelen van deze aanpak:</strong> Door niet te investeren vermijd je 
                    het risico van koersdalingen en marktturbulentie. Je totale kosten zijn volledig 
                    voorspelbaar: €{totalCost.toLocaleString('nl-NL')}, bestaande uit je oorspronkelijke 
                    schuld plus €{interestAtEnd.toLocaleString('nl-NL', { maximumFractionDigits: 0 })} aan rente. Deze zekerheid 
                    kan waardevol zijn, vooral als je een stabiele financiële planning prefereert.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Section 3: Betaalschema */}
        <div id="betaalschema" style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto 40px auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          padding: '50px 40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 'auto'
        }}>
          <h1 style={{
            fontSize: '42px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Betaalschema
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '30px'
          }}>
            Gedetailleerd overzicht van jouw jaarlijkse aflossingen
          </p>

          {/* Merged responsive table */}
          <div style={{ 
            margin: '0 auto 30px auto', 
            width: '100%', 
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'visible'
          }}>
            <table style={{ 
              borderCollapse: 'collapse', 
              width: '100%', 
              minWidth: '1200px',
              tableLayout: 'auto', 
              fontSize: 16, 
              wordBreak: 'break-word', 
              overflowWrap: 'anywhere' 
            }}>
              <colgroup>
                <col style={{ minWidth: '70px', width: 'auto' }} />
                <col style={{ minWidth: '110px', width: 'auto' }} />
                <col style={{ minWidth: '138px', width: 'auto' }} />
                <col style={{ minWidth: '138px', width: 'auto' }} />
                <col style={{ minWidth: '130px', width: 'auto' }} />
                <col style={{ minWidth: '148px', width: 'auto' }} />
                <col style={{ minWidth: '148px', width: 'auto' }} />
                <col style={{ minWidth: '120px', width: 'auto' }} />
                <col style={{ minWidth: '130px', width: 'auto' }} />
                <col style={{ minWidth: '138px', width: 'auto' }} />
                <col style={{ minWidth: '110px', width: 'auto' }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: '#3399FF', borderBottom: '2px solid #E5E7EB' }}>
                  <th colSpan={2} style={{ padding: '8px 10px', color: '#FFFFFF', textAlign: 'center' }}>Beginwaardes</th>
                  <th colSpan={3} style={{ padding: '8px 10px', color: '#FFFFFF', textAlign: 'center' }}>Betaaloverzicht</th>
                  <th colSpan={3} style={{ padding: '8px 10px', color: '#FFFFFF', textAlign: 'center' }}>Jaarlijks opgetelde betalingen</th>
                  <th colSpan={3} style={{ padding: '8px 10px', color: '#FFFFFF', textAlign: 'center' }}>Jaarlijkse eindwaardes</th>
                </tr>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'left', fontWeight: 600 }}>Jaar</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totale schuld</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Maandelijkse aflossing</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Maandelijkse investering</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Eenmalige aflossingen</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totale aflossingen</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totaal geïnvesteerd</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totale kosten</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Waarde investeringen</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totaal betaalde rente</th>
                  <th style={{ padding: '12px 10px', color: '#374151', textAlign: 'right', fontWeight: 600 }}>Totale schuld</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Use total term (stelsel + delay) if delay is enabled, otherwise use repaymentSystem
                  const maxYearToShow = (enableDelay && totalTermYears) ? totalTermYears : repaymentSystem;
                  const filteredRows = chartData.rows.filter(row => row.year <= maxYearToShow);
                  
                  // Find the index where loan is fully paid off
                  // Hard rule: if at the end of a year the total debt (Totale schuld) is 0, stop showing additional years
                  // Check both the starting principal and the end balance (Totale schuld column)
                  // Also check if investments exceed debt at end of year
                  // If any of these conditions are met, stop showing rows after that
                  let paidOffIndex = -1;
                  for (let i = 0; i < filteredRows.length; i++) {
                    const row = filteredRows[i];
                    const principal = row.principal || 0;
                    const nextRow = filteredRows[i + 1];
                    const endBalance = nextRow ? (nextRow.principal || 0) : 0;
                    const investmentAtEndOfYear = row.investment != null ? row.investment : 0;
                    
                    // Hard rule: If the end balance (Totale schuld) is 0 or very close to 0, loan is paid off
                    if (endBalance <= 0.01) {
                      paidOffIndex = i;
                      break;
                    }
                    
                    // Als waarde investeringen einde jaar >= totale schuld einde jaar: dat is PER DEFINITIE het laatste jaar
                    if (enableInvestment && investmentAtEndOfYear > 0 && investmentAtEndOfYear >= endBalance && endBalance > 0.01) {
                      paidOffIndex = i;
                      break;
                    }
                    
                    if (principal <= 0.01) {
                      paidOffIndex = i;
                      break;
                    }
                  }
                  
                  // Only show rows up to and including the paid-off row (if found)
                  // Hard rule: no additional years after debt is 0
                  const rowsToShow = paidOffIndex >= 0 
                    ? filteredRows.slice(0, paidOffIndex + 1)
                    : filteredRows;
                  
                  return rowsToShow.map((row, index, array) => {
                      // Display year as integer (no decimals), starting from 1
                      const displayYear = Math.round(row.year) + 1;
                      
                      // Check if this is a delay year
                      // Use row.year (0-based) for calculations, displayYear (1-based) only for display
                      const delayMonthsTotal = (enableDelay && delayYears) ? (delayYears * 12 + (delayMonths || 0)) : 0;
                      const yearStartMonth = Math.round(row.year) * 12; // Use 0-based year for calculations
                      const isDelayYear = enableDelay && yearStartMonth < delayMonthsTotal;
                      
                      // During delay period, payment is 0
                      const monthlyPaymentThisYear = isDelayYear ? 0 : (row.payment || monthlyPayment);
                      // Calculate investment contribution with growth if enabled
                      // Investments are now completely separate and don't affect loan payments
                      let investmentContribution = monthlyInvestment;
                      if (enableGrowth && !isDelayYear) {
                        // Calculate years elapsed since delay period ended (not since year 0)
                        // Use row.year (0-based) for calculations
                        const calcYear = Math.round(row.year);
                        const yearsElapsed = Math.max(0, calcYear - Math.floor(delayMonthsTotal / 12));
                        const investmentGrowthRateValue = getInvestmentGrowthRateValue();
                        investmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, yearsElapsed);
                      }
                      const investmentPayment = enableInvestment ? investmentContribution : 0;
                      // Loan payment is always the full monthly payment (investments don't reduce it)
                      const duoPayment = monthlyPaymentThisYear;
                      
                      const nextRow = array[index + 1];
                      // Gebruik schuld uit PortalPage data voor consistentie
                      // Deze waarde is al correct berekend volgens de PortalPage methodiek
                      const endBalance = nextRow ? (nextRow.principal || 0) : 0;
                      
                      // Check if this is the last row (repayment happens)
                      const isLastRow = index === rowsToShow.length - 1;
                      
                      // Calculate investment value at the end of this year using PortalPage methodiek
                      // This is the value after exactly (year number + 1) * 12 months (12, 24, 36, etc.)
                      // For year 0: value after 12 months
                      // For year 1: value after 24 months, etc.
                      let endInvestment = 0;
                      
                      if (enableInvestment) {
                        // Calculate months at end of this year
                        // Use row.year (0-based) for calculations: Year 0 ends at month 12, year 1 ends at month 24, etc.
                        const calcYear = Math.round(row.year);
                        const monthsAtEndOfYear = (calcYear + 1) * 12;
                        
                        // For last row, use the actual payoff month if earlier
                        if (isLastRow && payoffMonths < monthsAtEndOfYear) {
                          endInvestment = calculateInvestmentValueAtMonth(payoffMonths);
                        } else {
                          // Calculate value at end of year (12, 24, 36 months, etc.)
                          endInvestment = calculateInvestmentValueAtMonth(monthsAtEndOfYear);
                        }
                      }
                      
                      // Check if investments exceed debt (repayment via investments)
                      const repaidByInvestment = enableInvestment && (
                        (endInvestment > 0 && endInvestment >= endBalance && endBalance > 0.01) ||
                        (endInvestment > 0 && endInvestment >= (row.principal || 0) && (row.principal || 0) > 0.01)
                      );
                      
                      // Calculate actual months paid in this year based on when loan was paid off
                      // During delay period, no months are paid
                      const monthsPaidInYear = isDelayYear ? 0 : Math.min(12, Math.max(0, payoffMonths - yearStartMonth));
                      
                      // Totaal betaalde rente: gebruik de waarde uit PortalPage data voor consistentie
                      // Deze waarde is al correct berekend volgens de PortalPage methodiek
                      let endInterest = 0;
                      if (isLastRow && interestAtEnd != null && (endBalance <= 0.01 || repaidByInvestment)) {
                        // Last row: use exact interest paid at the moment of payoff
                        endInterest = interestAtEnd;
                      } else {
                        // Other rows: use the interest from the row (end of year) which is already cumulative
                        endInterest = row.interest != null ? row.interest : 0;
                      }
                      // Cumulatieve rente begin jaar (voor formule totale schuld eind)
                      const startInterest = index > 0 ? (array[index - 1].interest ?? 0) : 0;
                      
                      // Totaal betaald cumulatief: totale cumulatieve maandelijkse aflossingen plus totale cumulatieve investeringen
                      // Dit is de som van alle maandelijkse aflossingen + alle maandelijkse investeringen tot einde van dit jaar
                      let cumulativeAflossingen = 0; // Totale cumulatieve maandelijkse aflossingen
                      let cumulativeInvesteringen = 0; // Totale cumulatieve investeringen
                      
                      // Bereken voor alle jaren tot en met huidig jaar
                      for (let i = 0; i <= index; i++) {
                        const calcRow = array[i];
                        const calcYear = Math.round(calcRow.year);
                        const calcYearStartMonth = calcYear * 12;
                        const calcIsDelayYear = enableDelay && calcYearStartMonth < delayMonthsTotal;
                        const calcIsLastRow = i === index;
                        
                        if (!calcIsDelayYear) {
                          const calcMonthlyPayment = calcRow.payment || monthlyPayment;
                          
                          // Calculate investment contribution with growth if enabled
                          // Investments are now completely separate and don't affect loan payments
                          let calcInvestmentContribution = monthlyInvestment;
                          if (enableGrowth && !calcIsDelayYear) {
                            const calcYearsElapsed = Math.max(0, calcYear - Math.floor(delayMonthsTotal / 12));
                            const investmentGrowthRateValue = getInvestmentGrowthRateValue();
                            calcInvestmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, calcYearsElapsed);
                          }
                          const calcInvestmentPayment = enableInvestment ? calcInvestmentContribution : 0;
                          // Loan payment is always the full monthly payment (investments don't reduce it)
                          const calcAflossingPayment = calcMonthlyPayment;
                          
                          // Bereken aantal maanden betaald in dit jaar
                          const calcMonthsPaid = calcIsLastRow 
                            ? monthsPaidInYear 
                            : Math.min(12, Math.max(0, payoffMonths - calcYearStartMonth));
                          
                          // Totale cumulatieve maandelijkse aflossingen: som van (maandelijkse aflossing * maanden)
                          cumulativeAflossingen += calcAflossingPayment * calcMonthsPaid;
                          
                          // Totale cumulatieve investeringen: som van (maandelijkse investering * maanden)
                          cumulativeInvesteringen += calcInvestmentPayment * calcMonthsPaid;
                        }
                      }
                      
                      // Totaal betaald cumulatief = totale cumulatieve maandelijkse aflossingen + totale cumulatieve investeringen
                      let displayCumulativePaid = cumulativeAflossingen + cumulativeInvesteringen;
                      
                      // Totaal betaald: waarde van vorige rij + totale maandelijkse aflossingen dit jaar (exclusief investeringen) + totale maandelijkse investeringen dit jaar
                      // Maandelijkse aflossingen zijn exclusief investeringen (duoPayment)
                      // Bereken totale maandelijkse aflossingen dit jaar (exclusief investeringen)
                      const totaleAflossingenDitJaar = isDelayYear ? 0 : (duoPayment * monthsPaidInYear);
                      // Bereken totale maandelijkse investeringen dit jaar (los toegevoegd)
                      const totaleInvesteringenDitJaar = isDelayYear ? 0 : (investmentPayment * monthsPaidInYear);
                      
                      // Bereken cumulatieve waarde tot en met vorige rij
                      // Dit is de som van: (maandelijkse aflossingen exclusief investeringen * maanden) + (maandelijkse investeringen * maanden)
                      let vorigeCumulatief = 0;
                      if (index > 0) {
                        for (let i = 0; i < index; i++) {
                          const calcRow = array[i];
                          const calcYear = Math.round(calcRow.year);
                          const calcYearStartMonth = calcYear * 12;
                          const calcIsDelayYear = enableDelay && calcYearStartMonth < delayMonthsTotal;
                          const calcIsLastRow = i === index - 1;
                          
                          if (!calcIsDelayYear) {
                            const calcMonthlyPayment = calcRow.payment || monthlyPayment;
                            
                            // Calculate investment contribution with growth if enabled
                            // Investments are now completely separate and don't affect loan payments
                            let calcInvestmentContribution = monthlyInvestment;
                            if (enableGrowth && !calcIsDelayYear) {
                              const calcYearsElapsed = Math.max(0, calcYear - Math.floor(delayMonthsTotal / 12));
                              const investmentGrowthRateValue = getInvestmentGrowthRateValue();
                              calcInvestmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, calcYearsElapsed);
                            }
                            const calcInvestmentPayment = enableInvestment ? calcInvestmentContribution : 0;
                            // Loan payment is always the full monthly payment (investments don't reduce it)
                            const calcAflossingPayment = calcMonthlyPayment;
                            
                            const calcMonthsPaid = calcIsLastRow 
                              ? Math.min(12, Math.max(0, payoffMonths - calcYearStartMonth))
                              : Math.min(12, Math.max(0, payoffMonths - calcYearStartMonth));
                            
                            // Optellen: aflossingen (full payment) + investeringen (los toegevoegd)
                            vorigeCumulatief += (calcAflossingPayment * calcMonthsPaid) + (calcInvestmentPayment * calcMonthsPaid);
                          }
                        }
                      }
                      
                      // Totaal betaald = vorige waarde + aflossingen dit jaar (exclusief investeringen) + investeringen dit jaar (los toegevoegd)
                      const totaalBetaald = vorigeCumulatief + totaleAflossingenDitJaar + totaleInvesteringenDitJaar;
                      
                      // Totale eenmalige aflossingen dit jaar: alle ingevoerde eenmalige aflossingen die in dit jaar vallen
                      // year 0 = Jaarlijks (telt in elk jaar mee), year 1 = jaar 1, year 2 = jaar 2, etc. (1-based)
                      const reps = Array.isArray(oneTimeRepayments) ? oneTimeRepayments : [];
                      const oneTimeThisYear = reps
                        .filter((r) => r && (Number(r.amount) || 0) > 0)
                        .filter((r) => {
                          const y = Number(r.year);
                          if (Number.isNaN(y)) return false;
                          if (y === 0) return true;  // Jaarlijks: in elk jaar
                          return y === displayYear;  // Specifiek jaar: alleen in dat jaar
                        })
                        .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

                      // Totaal geïnvesteerd: cumulatief; gebruik werkelijke bedragen uit schedule (tableData) indien aanwezig (o.a. laatste maand 0 of deel)
                      let totaalGeinvesteerd = 0;
                      for (let i = 0; i <= index; i++) {
                        const calcRow = array[i];
                        if (calcRow.actualInvestmentThisYear != null) {
                          totaalGeinvesteerd += calcRow.actualInvestmentThisYear;
                        } else {
                          const calcYear = Math.round(calcRow.year);
                          const calcYearStartMonth = calcYear * 12;
                          const calcIsDelayYear = enableDelay && calcYearStartMonth < delayMonthsTotal;
                          const calcIsLastRow = i === index;
                          if (!calcIsDelayYear) {
                            let calcInvestmentContribution = monthlyInvestment;
                            if (enableGrowth) {
                              const calcYearsElapsed = Math.max(0, calcYear - Math.floor(delayMonthsTotal / 12));
                              const investmentGrowthRateValue = getInvestmentGrowthRateValue();
                              calcInvestmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, calcYearsElapsed);
                            }
                            const calcInvestmentPayment = enableInvestment ? calcInvestmentContribution : 0;
                            const calcMonthsPaid = calcIsLastRow ? monthsPaidInYear : Math.min(12, Math.max(0, payoffMonths - calcYearStartMonth));
                            totaalGeinvesteerd += calcInvestmentPayment * calcMonthsPaid;
                          }
                        }
                      }
                      
                      // Totale aflossingen = maandelijkse aflossingen + eenmalige aflossingen (cumulatief); gebruik werkelijke aflossingen uit schedule indien aanwezig
                      let totaleAflossingenOnly = 0;
                      let cumulatiefEenmalige = 0;
                      for (let i = 0; i <= index; i++) {
                        const calcRow = array[i];
                        const calcYear = Math.round(calcRow.year);
                        const calcDisplayYear = calcYear + 1;
                        const calcOneTime = (Array.isArray(oneTimeRepayments) ? oneTimeRepayments : [])
                          .filter((r) => r && (Number(r.amount) || 0) > 0)
                          .filter((r) => {
                            const y = Number(r.year);
                            if (Number.isNaN(y)) return false;
                            if (y === 0) return true;
                            return y === calcDisplayYear;
                          })
                          .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
                        cumulatiefEenmalige += calcOneTime;
                        if (calcRow.actualLoanPaymentsThisYear != null) {
                          totaleAflossingenOnly += calcRow.actualLoanPaymentsThisYear;
                        } else {
                          const calcYearStartMonth = calcYear * 12;
                          const calcIsDelayYear = enableDelay && calcYearStartMonth < delayMonthsTotal;
                          const calcIsLastRow = i === index;
                          if (!calcIsDelayYear) {
                            const calcMonthlyPayment = calcRow.payment || monthlyPayment;
                            const calcMonthsPaid = calcIsLastRow ? monthsPaidInYear : Math.min(12, Math.max(0, payoffMonths - calcYearStartMonth));
                            totaleAflossingenOnly += calcMonthlyPayment * calcMonthsPaid;
                          }
                        }
                      }
                      // Sluitpost laatste rij: totale aflossingen = totale kosten − totaal geïnvesteerd, zodat som klopt
                      let totaleAflossingen = totaleAflossingenOnly + cumulatiefEenmalige;
                      let totaleKosten = totaleAflossingen + totaalGeinvesteerd;
                      if (isLastRow && totalCost != null) {
                        totaleKosten = totalCost;
                        totaleAflossingen = totalCost - totaalGeinvesteerd;
                      }

                      // Totale schuld (eind) = totale schuld (begin) − (maandelijkse aflossing × 12) − eenmalige aflossingen + (totaal betaalde rente n − rente n−1)
                      const interestDelta = endInterest - startInterest;
                      const totaleSchuldEind = (row.principal || 0) - totaleAflossingenDitJaar - oneTimeThisYear + interestDelta;
                      const displayTotaleSchuld = isLastRow ? 0 : Math.max(0, Math.round(totaleSchuldEind));
                      
                      return (
                        <React.Fragment key={row.year}>
                          <tr style={{ borderBottom: isLastRow ? '2px solid #10B981' : '1px solid #F3F4F6', backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFBFC' }}>
                            <td style={{ padding: '12px 10px', color: '#1F2937' }}>{displayYear}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#3399FF', fontWeight: 600 }}>€{(row.principal || 0).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#374151' }}>
                              {isDelayYear ? '—' : `€${monthlyPaymentThisYear.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#374151' }}>
                              {isDelayYear ? '—' : `€${investmentPayment.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#374151' }}>
                              {oneTimeThisYear > 0 ? `€${oneTimeThisYear.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}` : '—'}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#3399FF', fontWeight: 600 }}>
                              €{totaleAflossingen.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#3399FF', fontWeight: 600 }}>€{totaalGeinvesteerd.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#3399FF', fontWeight: 600 }}>€{totaleKosten.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#374151' }}>€{(endInvestment || 0).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#374151' }}>€{(endInterest || 0).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: '12px 10px', textAlign: 'right', color: '#3399FF', fontWeight: 600 }}>€{displayTotaleSchuld.toLocaleString('nl-NL', { maximumFractionDigits: 0 })}</td>
                          </tr>
                          {isLastRow && (
                            <tr>
                              <td colSpan="11" style={{ padding: '8px 10px', backgroundColor: '#F0FDF4', borderBottom: '2px solid #10B981' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10B981', fontWeight: 600, fontSize: '14px' }}>
                                  <span>✓</span>
                                  <span>
                                    {repaidByInvestment 
                                      ? 'Schuld volledig afbetaald via investeringen' 
                                      : 'Schuld volledig afbetaald'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    });
                })()}
              </tbody>
            </table>
          </div>

          {/* Explanatory text */}
          <div style={{
            backgroundColor: '#F9FAFB',
            border: '2px solid #E5E7EB',
            borderRadius: '16px',
            padding: '32px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1F2937',
              margin: '0 0 20px 0'
            }}>
              Toelichting op het betaalschema
            </h3>
            
            <div style={{
              fontSize: '18px',
              color: '#374151',
              lineHeight: '1.8'
            }}>
              <p style={{ marginTop: 0, marginBottom: '16px' }}>
                <strong>Hoe werkt dit schema?</strong> Deze tabel geeft een volledig overzicht van jouw aflossingen per jaar. 
                Je begint met een maandelijkse betaling van €{monthlyPayment.toLocaleString('nl-NL')}, wat neerkomt op 
                €{(monthlyPayment * 12).toLocaleString('nl-NL')} per jaar
                {strategyData?.enableGrowth && Number(strategyData?.growthRate || 0) > 0 ? (
                  <>, wat jaarlijks met {Number(strategyData.growthRate)}% stijgt.</>
                ) : (
                  <>.</>
                )} Dit bedrag wordt opgesplitst in verschillende 
                componenten die samen je financiële strategie bepalen.
              </p>

              <p style={{ marginBottom: '16px' }}>
                <strong>Kolom uitleg:</strong> De kolom "Maandelijkse Aflossing" toont hoeveel je elke maand betaalt. 
                "DUO Aflossing" is het minimale bedrag van €{minPayment.toLocaleString('nl-NL')} dat je verplicht 
                bent aan DUO te betalen - dit bedrag gaat altijd direct van je schuld af. {enableInvestment ? 
                `De kolom "Investering" toont het bedrag dat je maandelijks investeert in de aandelenmarkt, 
                wat €${Math.round(monthlyInvestment).toLocaleString('nl-NL')} per maand is 
                (€${investmentAmount.toFixed(0)} per maand).` : 
                'Je hebt ervoor gekozen om niet te investeren, dus al je betalingen gaan direct naar het aflossen van je schuld.'}
              </p>

              <p style={{ marginBottom: '16px' }}>
                <strong>Schuldontwikkeling:</strong> De kolommen "Schuld Begin" en "Schuld Eind" tonen je openstaande 
                schuld aan het begin en einde van elk jaar. Door de {repaymentSystem === 15 ? '2,21%' : '2,57%'} 
                jaarlijkse rente die maandelijks wordt berekend, groeit je schuld eerst een beetje voordat je 
                aflossing wordt afgetrokken. Je kunt zien hoe je schuld elk jaar afneemt, van een beginbedrag van 
                €{totalDebt.toLocaleString('nl-NL')} naar uiteindelijk €0 in jaar {payoffYears}.
              </p>

              {enableInvestment && (
                <p style={{ marginBottom: '16px' }}>
                  <strong>Impact van investeren:</strong> Door te kiezen voor een gemengde strategie bouw je niet 
                  alleen vermogen op via investeringen, maar betaal je ook minder rente omdat je schuld sneller 
                  afneemt. {chartData.payoffByInvestment ? 
                    `Op het moment dat je investeringen gelijk zijn aan je resterende schuld, kun je de lening in 
                    één keer aflossen. Dit gebeurt in jaar ${Math.floor(payoffMonths / 12) + 1}, maanden eerder dan 
                    wanneer je alleen zou aflossen.` :
                    `Aan het einde heb je naast een schuldvrije situatie ook een investeringsportefeuille opgebouwd.`
                  }
                </p>
              )}

              <p style={{ marginBottom: 0 }}>
                <strong>Let op:</strong> De bedragen in deze tabel zijn gebaseerd op constante maandelijkse betalingen 
                en een vast rentepercentage. In de praktijk kunnen je betalingen variëren, bijvoorbeeld als je 
                salaris stijgt of als je besluit extra af te lossen. Ook kan DUO de rente aanpassen. Dit schema 
                geeft een indicatie van je financiële planning, maar blijft een voorspelling. Het is belangrijk 
                om regelmatig je aflossingen te monitoren en je strategie aan te passen waar nodig.
              </p>
            </div>
          </div>
        </div>

        {/* Section 4: FAQ */}
        <div id="faq" style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto 0 auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          padding: '50px 40px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          minHeight: 'auto'
        }}>
          <h1 style={{
            fontSize: '42px',
            fontWeight: '700',
            color: '#1F2937',
            margin: '0 0 16px 0'
          }}>
            Veelgestelde Vragen
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#6B7280',
            lineHeight: '1.6',
            marginBottom: '40px'
          }}>
            Antwoorden op de meest gestelde vragen over je aflosstrategie
          </p>

          {/* FAQ Items */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '28px'
          }}>
            {/* FAQ 1 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Wat is het verschil tussen de 15-jaars en 35-jaars regeling?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                De 15-jaars en 35-jaars regelingen bepalen hoe lang je de tijd hebt om je studieschuld af te lossen. 
                Bij de 15-jaars regeling geldt een rentepercentage van 2,21% per jaar, terwijl de 35-jaars regeling 
                een iets hoger percentage van 2,57% heeft. De keuze tussen deze regelingen hangt af van wanneer je 
                bent begonnen met studeren. Als je voor 2023 bent begonnen, valt je onder de 35-jaars regeling. 
                Na 2023 geldt de nieuwe 15-jaars regeling. Het is belangrijk om te weten welke regeling op jou van 
                toepassing is, omdat dit de rentekosten en je aflossingstermijn beïnvloedt.
              </p>
            </div>

            {/* FAQ 2 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Waarom zou ik kiezen voor investeren naast aflossen?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                Door een deel van je maandelijkse budget te investeren in plaats van alles direct af te lossen, 
                kun je profiteren van het potentieel hogere rendement van de aandelenmarkt (verwacht gemiddeld 6% per jaar, conservatieve schatting inclusief transactiekosten, niet gegarandeerd) 
                ten opzichte van de rente op je studieschuld (2,21% of 2,57%). Dit betekent dat je vermogen sneller 
                kan groeien dan je schuld, wat op termijn kan leiden tot lagere totale kosten. Belangrijk: je moet 
                altijd eerst je minimale DUO-aflossing voldoen, en alleen het bedrag daarboven kun je investeren. 
                Deze strategie heeft wel risico's, omdat aandelenkoersen kunnen fluctueren en het rendement niet gegarandeerd is.
              </p>
            </div>

            {/* FAQ 3 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Wat betekent "afbetaald via investering"?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                Als je kiest voor een investeringsstrategie, bouw je parallel aan het aflossen van je schuld ook 
                een investeringsportefeuille op. Op een bepaald moment kunnen je investeringen een waarde bereiken 
                die gelijk is aan of hoger is dan je resterende studieschuld. Op dat moment kun je ervoor kiezen om 
                je volledige lening in één keer af te lossen met het geld uit je investeringen. Dit gebeurt vaak 
                eerder dan wanneer je alleen zou aflossen, omdat je investeringen sneller groeien (verwacht gemiddeld 6% rendement, conservatieve schatting inclusief transactiekosten, niet gegarandeerd) 
                dan de rente op je schuld oploopt (2,21-2,57%). Het moment waarop dit gebeurt, wordt in de grafiek 
                aangegeven met een groen vinkje. Let op: dit rendement is niet gegarandeerd en markten kunnen fluctueren.
              </p>
            </div>

            {/* FAQ 4 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Hoe kan ik beginnen met investeren?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: '0 0 12px 0'
              }}>
                <em>Let op: Dit is geen beleggingsadvies, maar alleen informatief.</em> In Nederland zijn er 
                verschillende gereguleerde online brokers waar je eenvoudig kunt beginnen met investeren. 
                Populaire opties zijn:
              </p>
              <ul style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: '0 0 12px 0',
                paddingLeft: '24px'
              }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>DEGIRO</strong> - Een Nederlandse online broker met lage kosten en toegang tot 
                  internationale markten. Geschikt voor beginners en ervaren beleggers.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Saxo Bank</strong> - Een internationale bank met een uitgebreid platform en 
                  professionele tools. Iets hogere kosten maar meer service en ondersteuning.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>BinckBank (nu Saxo)</strong> - Nederlandse tak van Saxo Bank, bekend om 
                  toegankelijkheid en goede klantenservice.
                </li>
              </ul>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: '0 0 12px 0'
              }}>
                <strong>ETFs (Exchange Traded Funds)</strong> zijn beleggingsfondsen die een hele markt of index 
                volgen, zoals de AEX, S&P 500, of MSCI World. Deze producten worden vaak gebruikt door particuliere 
                beleggers die een investeringsstrategie zoals hierboven beschreven willen toepassen. Kenmerken van ETFs:
              </p>
              <ul style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0,
                paddingLeft: '24px'
              }}>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Diversificatie:</strong> Een ETF investeert in honderden of duizenden bedrijven tegelijk, 
                  waardoor het risico wordt gespreid over meerdere partijen.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Kostenstructuur:</strong> ETFs kennen over het algemeen lagere beheerkosten dan actief 
                  beheerde fondsen.
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <strong>Marktbrede blootstelling:</strong> Een ETF volgt een index, waardoor je niet afhankelijk 
                  bent van de prestaties van individuele bedrijven.
                </li>
                <li>
                  <strong>Toegankelijkheid:</strong> ETFs vereisen geen selectie van individuele aandelen; de 
                  samenstelling volgt automatisch de onderliggende index.
                </li>
              </ul>
            </div>

            {/* FAQ 5 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Wat zijn de risico's van een investeringsstrategie?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                Hoewel investeren aantrekkelijk kan zijn, zijn er ook risico's. De aandelenmarkt kan op korte 
                termijn sterk fluctueren, en je investeringen kunnen tijdelijk in waarde dalen. Er is geen garantie 
                dat je het verwachte rendement van 6% per jaar behaalt - in sommige jaren kan het rendement lager 
                zijn of zelfs negatief. Daarom is het cruciaal dat je altijd je minimale DUO-aflossing blijft voldoen 
                en alleen investeert met geld dat je voor de middellange tot lange termijn (5+ jaar) kunt missen. 
                Investeer nooit met geld dat je op korte termijn nodig hebt. Bij twijfel kun je ook kiezen voor een 
                lager investeringspercentage om het risico te beperken.
              </p>
            </div>

            {/* FAQ 6 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Kan ik mijn strategie later nog aanpassen?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                Absoluut! Je aflossings- en investeringsstrategie is niet in steen gebeiteld. Je kunt op elk moment 
                besluiten om meer of minder af te lossen, of om je investeringspercentage te verhogen of verlagen. 
                Levensomstandigheden veranderen - misschien krijg je een loonsverhoging, of juist tegenslag - en 
                je strategie kan hierop worden aangepast. We raden aan om minimaal eens per jaar je situatie te 
                herzien en je strategie bij te stellen indien nodig. Via het Slimmer af platform kun je altijd een nieuwe 
                berekening maken met aangepaste parameters.
              </p>
            </div>

            {/* FAQ 7 */}
            <div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1F2937',
                margin: '0 0 12px 0'
              }}>
                Wat gebeurt er als ik een maand niet kan betalen?
              </h3>
              <p style={{
                fontSize: '18px',
                color: '#374151',
                lineHeight: '1.7',
                margin: 0
              }}>
                Het is belangrijk om altijd minimaal je verplichte DUO-aflossing te blijven betalen. Als je 
                financiële problemen hebt, neem dan zo snel mogelijk contact op met DUO. Zij kunnen soms een 
                betalingsregeling treffen of tijdelijk uitstel verlenen bij bijzondere omstandigheden zoals 
                werkloosheid of ziekte. Voor het investeringsdeel heb je meer flexibiliteit - als het even 
                financieel krap is, kun je ervoor kiezen om tijdelijk minder of niet te investeren en je volledige 
                betaling naar de DUO-aflossing te laten gaan. Communicatie met DUO is essentieel om problemen 
                te voorkomen.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;
